/**
 * Response Option Card Component
 * 
 * Displays clickable option cards for AI response choices.
 * When AI responds with options, these cards allow users to select their choice.
 */
import { ChevronRight, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export interface ResponseOption {
  id: string;
  title: string;
  description?: string;
  icon?: LucideIcon;
  value: string; // The message to send when selected
}

interface ResponseOptionCardProps {
  option: ResponseOption;
  onClick: (option: ResponseOption) => void;
  isLoading?: boolean;
  variant?: "default" | "compact";
}

export const ResponseOptionCard = ({
  option,
  onClick,
  isLoading = false,
  variant = "default",
}: ResponseOptionCardProps) => {
  const Icon = option.icon;

  if (variant === "compact") {
    return (
      <button
        onClick={() => onClick(option)}
        disabled={isLoading}
        className={cn(
          "w-full px-3 py-2 rounded-md border border-gray6 bg-white cursor-pointer text-left",
          "hover:border-greentab hover:bg-green4 transition-all duration-200",
          "focus:outline-none focus:ring-2 focus:ring-greentab focus:ring-offset-1",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          "flex items-center justify-between gap-2 group"
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          {Icon && (
            <Icon className="h-4 w-4 text-gray4 group-hover:text-green2 flex-shrink-0" />
          )}
          <span className="text-sm text-black1 truncate">{option.title}</span>
        </div>
        <ChevronRight className="h-4 w-4 text-gray5 group-hover:text-greentab flex-shrink-0" />
      </button>
    );
  }

  return (
    <button
      onClick={() => onClick(option)}
      disabled={isLoading}
      className={cn(
        "w-full p-3 rounded-lg border border-gray6 bg-white cursor-pointer text-left",
        "hover:border-greentab hover:bg-green4 transition-all duration-200",
        "focus:outline-none focus:ring-2 focus:ring-greentab focus:ring-offset-1",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        "group"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          {Icon && (
            <div
              className={cn(
                "h-8 w-8 rounded-md border border-gray5 bg-white flex items-center justify-center flex-shrink-0",
                "group-hover:border-greentab group-hover:bg-green4 transition-colors duration-200"
              )}
            >
              <Icon
                className={cn(
                  "h-4 w-4 text-gray3",
                  "group-hover:text-green2 transition-colors duration-200"
                )}
              />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h4 className="font-medium text-black1 text-sm leading-tight">
              {option.title}
            </h4>
            {option.description && (
              <p className="text-gray4 text-xs mt-0.5 line-clamp-2">
                {option.description}
              </p>
            )}
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-gray5 group-hover:text-greentab flex-shrink-0 mt-0.5" />
      </div>
    </button>
  );
};

interface ResponseOptionsListProps {
  options: ResponseOption[];
  onSelect: (option: ResponseOption) => void;
  isLoading?: boolean;
  title?: string;
  variant?: "default" | "compact";
}

export const ResponseOptionsList = ({
  options,
  onSelect,
  isLoading = false,
  title,
  variant = "default",
}: ResponseOptionsListProps) => {
  if (options.length === 0) return null;

  return (
    <div className="mt-3">
      {title && (
        <p className="text-xs font-medium text-gray4 mb-2">{title}</p>
      )}
      <div className={cn("space-y-2", variant === "compact" && "space-y-1.5")}>
        {options.map((option) => (
          <ResponseOptionCard
            key={option.id}
            option={option}
            onClick={onSelect}
            isLoading={isLoading}
            variant={variant}
          />
        ))}
      </div>
    </div>
  );
};

